'use client';

import { Spinner } from '@/components/glitch-ui/spinner';
import { useState, useEffect, useMemo, useRef } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Search } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { usePostHog } from 'posthog-js/react';
import { StepsIndicator } from '@/components/glitch-ui/steps-indicator';
import { useToast } from '@/hooks/useToast';
import { useAtom, useAtomValue } from 'jotai';
import {
  organizationDataAtom,
  addToCampaignPreSelectionAtom,
  addToCampaignCompetitorIdAtom,
} from '@/store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { businessQueryOptions, competitorsByBusinessQueryOptions } from '@/lib/query-options';
import { fetchCampaigns, addCompetitorsToCampaign } from '@/lib/apis';
import type { CampaignData } from '@/lib/types';

export const Route = createFileRoute('/_authenticated/add-to-campaign')({
  component: AddToCampaign,
});

enum ADD_TO_CAMPAIGN_STEP {
  SELECT_CAMPAIGN = 'SELECT_CAMPAIGN',
  SELECT_COMPETITORS = 'SELECT_COMPETITORS',
}

function AddToCampaign() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const posthog = usePostHog();
  const queryClient = useQueryClient();

  // #4554: when launched from a competitor campaign page, skip the
  // SELECT_CAMPAIGN step and pre-select the originating campaign. Snapshot the
  // atom on mount so subsequent updates (or other components clearing it) don't
  // flip behavior mid-wizard, then clear the atom so a fresh visit from
  // /competitors starts at SELECT_CAMPAIGN as normal.
  const [addToCampaignPreSelection, setAddToCampaignPreSelection] = useAtom(addToCampaignPreSelectionAtom);
  const initialPreSelectionRef = useRef(addToCampaignPreSelection);
  useEffect(() => {
    if (addToCampaignPreSelection) {
      setAddToCampaignPreSelection(null);
    }
  }, [addToCampaignPreSelection, setAddToCampaignPreSelection]);
  const launchedFromCampaign = initialPreSelectionRef.current;

  // Organization and business data
  const organizationData = useAtomValue(organizationDataAtom);
  const { data: business } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  // Competitors data
  const { data: competitors } = useQuery({
    ...competitorsByBusinessQueryOptions(business?.id || 0),
    enabled: !!business?.id,
  });

  // Filter competitors not already in a campaign
  const availableCompetitors = (competitors?.filter((c) => !c.campaign) || [])
    .sort((a, b) => a.competitor_brand_name.localeCompare(b.competitor_brand_name));

  // Competitor campaigns
  const [competitorCampaigns, setCompetitorCampaigns] = useState<CampaignData[]>([]);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(true);

  // State
  const [currentStep, setCurrentStep] = useState<ADD_TO_CAMPAIGN_STEP>(
    launchedFromCampaign ? ADD_TO_CAMPAIGN_STEP.SELECT_COMPETITORS : ADD_TO_CAMPAIGN_STEP.SELECT_CAMPAIGN,
  );
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(
    launchedFromCampaign?.campaignId ?? null,
  );
  const [selectedCompetitorIds, setSelectedCompetitorIds] = useState<Set<string>>(new Set());
  const [competitorSearchQuery, setCompetitorSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch competitor campaigns
  useEffect(() => {
    if (organizationData?.id) {
      setIsLoadingCampaigns(true);
      fetchCampaigns(organizationData.id)
        .then((campaigns) => {
          const filtered = campaigns.filter((c) => c.goal === 'competitor');
          setCompetitorCampaigns(filtered);
          // Auto-select if only one campaign
          if (filtered.length === 1) {
            setSelectedCampaignId(filtered[0].id);
          }
        })
        .catch((err) => {
          console.error('Error fetching campaigns:', err);
          toast({
            title: 'Error',
            description: 'Failed to load campaigns',
            variant: 'destructive',
          });
        })
        .finally(() => {
          setIsLoadingCampaigns(false);
        });
    }
  }, [organizationData?.id]);

  // Opened from one competitor's row (#6715), only that competitor is ticked;
  // opened with nothing named, every competitor is, as it always was. Read once
  // and cleared, so a later visit is not still carrying the old row.
  const [startWithCompetitorId, setStartWithCompetitorId] = useAtom(addToCampaignCompetitorIdAtom);
  const startWithCompetitorIdRef = useRef(startWithCompetitorId);
  useEffect(() => {
    if (startWithCompetitorId) setStartWithCompetitorId(null);
  }, [startWithCompetitorId, setStartWithCompetitorId]);

  const hasSetDefaultCompetitors = useRef(false);
  useEffect(() => {
    if (!hasSetDefaultCompetitors.current && availableCompetitors.length > 0) {
      hasSetDefaultCompetitors.current = true;
      const only = startWithCompetitorIdRef.current;
      const named = only ? availableCompetitors.filter((c) => c.id === only) : [];
      setSelectedCompetitorIds(new Set((named.length > 0 ? named : availableCompetitors).map((c) => c.id)));
    }
  }, [availableCompetitors]);

  // Filter competitors based on search
  const filteredCompetitors = useMemo(() => {
    if (!competitorSearchQuery.trim()) {
      return availableCompetitors;
    }
    const query = competitorSearchQuery.toLowerCase();
    return availableCompetitors.filter(
      (c) =>
        c.competitor_brand_name.toLowerCase().includes(query) ||
        (c.primary_website && c.primary_website.toLowerCase().includes(query))
    );
  }, [availableCompetitors, competitorSearchQuery]);

  const showCompetitorSearch = availableCompetitors.length > 5;

  // Toggle competitor selection
  const toggleCompetitor = (competitorId: string) => {
    setSelectedCompetitorIds((prev) => {
      const next = new Set(prev);
      if (next.has(competitorId)) {
        next.delete(competitorId);
      } else {
        next.add(competitorId);
      }
      return next;
    });
  };

  // Handle back
  const handleBack = () => {
    if (currentStep === ADD_TO_CAMPAIGN_STEP.SELECT_CAMPAIGN) {
      navigate({ to: '/business', hash: 'competitors' });
    } else if (launchedFromCampaign) {
      // SELECT_CAMPAIGN was skipped — back exits to the originating campaign.
      navigate({ to: '/campaigns/google/$campaignslug', params: { campaignslug: launchedFromCampaign.originSlug } });
    } else {
      setCurrentStep(ADD_TO_CAMPAIGN_STEP.SELECT_CAMPAIGN);
    }
  };

  // Handle next/submit
  const handleNext = async () => {
    if (currentStep === ADD_TO_CAMPAIGN_STEP.SELECT_CAMPAIGN) {
      setCurrentStep(ADD_TO_CAMPAIGN_STEP.SELECT_COMPETITORS);
    } else {
      // Submit
      await handleSubmit();
    }
  };

  // Submit - add competitors to campaign
  const handleSubmit = async () => {
    if (!selectedCampaignId || selectedCompetitorIds.size === 0) return;

    setIsSubmitting(true);

    try {
      const result = await addCompetitorsToCampaign(selectedCampaignId, Array.from(selectedCompetitorIds));

      // Invalidate caches
      queryClient.invalidateQueries({ queryKey: ['competitors'] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });

      posthog?.capture('competitors_added_to_campaign', {
        campaign_id: selectedCampaignId,
        competitors_added: result.added.length,
        competitors_skipped: result.skipped.length,
        keywords_added: result.keywords_added,
      });

      if (result.added.length > 0) {
        toast({
          title: 'Competitors Added',
          description: `Successfully added ${result.added.length} competitor(s) to campaign.`,
        });

        // Navigate to campaign
        const campaign = competitorCampaigns.find((c) => c.id === selectedCampaignId);
        if (campaign) {
          navigate({ to: '/campaigns/google/$campaignslug', params: { campaignslug: campaign.slug } });
        } else {
          navigate({ to: '/business', hash: 'competitors' });
        }
      } else {
        toast({
          title: 'No Changes',
          description: 'All selected competitors were already in campaigns.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add competitors';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      posthog?.capture('competitors_add_to_campaign_failed', { error: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step validation
  const isStepValid = () => {
    if (currentStep === ADD_TO_CAMPAIGN_STEP.SELECT_CAMPAIGN) {
      return selectedCampaignId !== null;
    }
    return selectedCompetitorIds.size > 0;
  };

  // Get step title
  const getStepTitle = () => {
    if (currentStep === ADD_TO_CAMPAIGN_STEP.SELECT_CAMPAIGN) {
      return 'Select Campaign';
    }
    return 'Select Competitors';
  };

  // Get button text
  const getNextButtonText = () => {
    if (currentStep === ADD_TO_CAMPAIGN_STEP.SELECT_COMPETITORS) {
      return 'ADD TO CAMPAIGN';
    }
    return 'CONTINUE';
  };

  return (
    <div className="relative p-8 w-full h-screen">
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 max-w-5xl w-full space-y-6 m-auto pt-8">
        {!launchedFromCampaign && (
          <StepsIndicator
            currentStep={currentStep === ADD_TO_CAMPAIGN_STEP.SELECT_CAMPAIGN ? 1 : 2}
            totalSteps={2}
          />
        )}
        <div className="flex justify-center">
          <div className="w-[60%]">
            <h1 className="text-3xl font-bold text-center mb-8">{getStepTitle()}</h1>
            <Card className="p-6">
              {/* Step 1: Select Campaign */}
              {currentStep === ADD_TO_CAMPAIGN_STEP.SELECT_CAMPAIGN && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                  className="space-y-4"
                >
                  <p className="text-gray-700 text-center mb-6">
                    Choose which competitor campaign to add competitors to.
                  </p>
                  {isLoadingCampaigns ? (
                    <div className="flex items-center justify-center py-8">
                      <Spinner size="lg" className="text-gray-500" />
                    </div>
                  ) : competitorCampaigns.length === 0 ? (
                    /* This flow adds to a campaign and only that (#6743): a
                       campaign is created on the campaign screens, where its
                       goal, budget and ads are decided, never as a side effect
                       of filing a competitor. */
                    <div className="text-center py-8">
                      <p className="text-gray-500">
                        There is no competitor campaign to add to. Create one from Campaigns first.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {competitorCampaigns.map((campaign) => (
                        <label
                          key={campaign.id}
                          className={`flex items-center space-x-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                            selectedCampaignId === campaign.id
                              ? 'border-[#290BD9] bg-[#290BD9]/10'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="campaign"
                            value={campaign.id}
                            checked={selectedCampaignId === campaign.id}
                            onChange={() => setSelectedCampaignId(campaign.id)}
                            className="sr-only"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900">{campaign.name}</div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {(() => {
                                const assignedCompetitors = competitors?.filter((c) => c.campaign?.id === campaign.id) || [];
                                if (assignedCompetitors.length > 0) {
                                  return (
                                    <span className="truncate block">
                                      {assignedCompetitors.map((c) => c.competitor_brand_name).join(', ')}
                                    </span>
                                  );
                                }
                                return <span>{campaign.ads?.length || 0} competitor(s)</span>;
                              })()}
                            </div>
                          </div>
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              selectedCampaignId === campaign.id
                                ? 'border-[#290BD9]'
                                : 'border-gray-300'
                            }`}
                          >
                            {selectedCampaignId === campaign.id && (
                              <div className="w-2.5 h-2.5 rounded-full bg-[#290BD9]" />
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {/* Step 2: Select Competitors */}
              {currentStep === ADD_TO_CAMPAIGN_STEP.SELECT_COMPETITORS && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                  className="space-y-4"
                >
                  <p className="text-gray-700">
                    Select competitors to add to the campaign. Only competitors not already in a campaign are shown.
                  </p>
                  {availableCompetitors.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {!competitors || competitors.length === 0
                        ? 'No competitors found. Generate competitors from the Competitors page first.'
                        : 'No available competitors. All competitors are already assigned to campaigns.'}
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center space-x-4 pl-4">
                        <Checkbox
                          checked={
                            selectedCompetitorIds.size === availableCompetitors.length && availableCompetitors.length > 0
                              ? true
                              : selectedCompetitorIds.size > 0
                                ? 'indeterminate'
                                : false
                          }
                          onCheckedChange={() => {
                            if (selectedCompetitorIds.size > 0) {
                              setSelectedCompetitorIds(new Set());
                            } else {
                              setSelectedCompetitorIds(new Set(availableCompetitors.map((c) => c.id)));
                            }
                          }}
                        />
                        {showCompetitorSearch && (
                          <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              placeholder="Search competitors..."
                              value={competitorSearchQuery}
                              onChange={(e) => setCompetitorSearchQuery(e.target.value)}
                              className="pl-10"
                            />
                          </div>
                        )}
                      </div>
                      <div className="max-h-[50vh] overflow-y-auto space-y-2">
                        {filteredCompetitors.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            No competitors found matching "{competitorSearchQuery}"
                          </div>
                        ) : (
                          filteredCompetitors.map((competitor) => (
                            <label
                              key={competitor.id}
                              className="flex items-center space-x-4 p-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                              <Checkbox
                                checked={selectedCompetitorIds.has(competitor.id)}
                                onCheckedChange={() => toggleCompetitor(competitor.id)}
                              />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">
                                  {competitor.competitor_brand_name}
                                </div>
                                {competitor.primary_website && (
                                  <div className="text-sm text-gray-500">
                                    {competitor.primary_website}
                                  </div>
                                )}
                              </div>
                            </label>
                          ))
                        )}
                      </div>
                      <div className="text-sm text-gray-500 text-center">
                        {selectedCompetitorIds.size} of {availableCompetitors.length} competitors selected
                      </div>
                    </>
                  )}
                </motion.div>
              )}
            </Card>

            <div className="flex justify-between items-center pt-6">
              <Button
                variant="outline"
                size="lg"
                onClick={handleBack}
                className="w-[216px] py-6 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border-gray-200 bg-white hover:bg-gray-50"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>BACK</span>
              </Button>

              <Button
                size="lg"
                disabled={!isStepValid() || isSubmitting || isLoadingCampaigns}
                onClick={handleNext}
                className="w-[216px] py-6 bg-[#290BD9] hover:bg-[#2309C5] shadow-lg flex items-center justify-center space-x-2"
              >
                <span>{isSubmitting ? 'ADDING...' : getNextButtonText()}</span>
                {!isSubmitting && <ArrowRight className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
